import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  App,
  AppApiProvider,
  AppConfigProvider,
  AuthProvider,
  QueryProvider,
  faviconUrl,
} from "@repo/app-ui";
import "@repo/app-ui/theme.css";
import { webAuthAdapter } from "./auth/webAuthAdapter";

// Set the document favicon from the shared, bundled asset (previously a static
// `/favicon.svg` in `public/`; now owned by `@repo/app-ui` so web and desktop
// share one source).
const favicon = document.createElement("link");
favicon.rel = "icon";
favicon.type = "image/svg+xml";
favicon.href = faviconUrl;
document.head.appendChild(favicon);

// Fail loudly at startup if the API base URL is missing or got inlined as
// `"undefined"` (Vite's behavior when a referenced VITE_* is unset). The
// alternative is silent: requests resolve to `/undefined/...` against the SPA
// host, return index.html, and openapi-fetch reports cryptic JSON parse
// errors instead of a clear configuration failure.
const rawApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
if (!rawApiBaseUrl || rawApiBaseUrl === "undefined") {
  throw new Error(
    "VITE_API_BASE_URL is not set. Configure it in the deploy environment before building the SPA.",
  );
}
const API_BASE_URL: string = rawApiBaseUrl;

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing #root element");
}

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider adapter={webAuthAdapter}>
        <QueryProvider>
          <AppApiProvider baseUrl={API_BASE_URL}>
            {/* On web, public share links use the current origin. */}
            <AppConfigProvider config={{ webBaseUrl: window.location.origin }}>
              <App />
            </AppConfigProvider>
          </AppApiProvider>
        </QueryProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
